(() => {
  const elementName = 'lpl-careers';

  if (customElements.get(elementName)) return;

  class LplCareers extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.controller = new AbortController();
      this.triggers = Array.from(this.querySelectorAll('[data-lpl-careers-trigger]'));
      this.dialogs = Array.from(this.querySelectorAll('[data-lpl-careers-dialog]'));
      const options = { signal: this.controller.signal };

      this.triggers.forEach((trigger) => {
        const dialogId = trigger.dataset.lplCareersDialogId;
        const candidateDialog = dialogId ? document.getElementById(dialogId) : null;
        const dialog = candidateDialog && this.contains(candidateDialog) ? candidateDialog : null;
        if (!dialog || typeof dialog.showModal !== 'function') return;

        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', dialog.id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', (event) => this.openFromTrigger(event, trigger), options);
      });

      this.dialogs.forEach((dialog) => {
        if (typeof dialog.showModal !== 'function' || typeof dialog.close !== 'function') return;
        const closeButton = dialog.querySelector('[data-lpl-careers-close]');
        if (closeButton) closeButton.addEventListener('click', () => dialog.close(), options);
        dialog.addEventListener('click', (event) => this.closeFromBackdrop(event, dialog), options);
        dialog.addEventListener('keydown', (event) => this.trapFocus(event, dialog), options);
        dialog.addEventListener('close', () => this.afterClose(dialog), options);
      });
    }

    disconnectedCallback() {
      if (!this.controller) return;
      this.controller.abort();
      this.controller = null;
      this.triggers?.forEach((trigger) => {
        trigger.removeAttribute('aria-haspopup');
        trigger.removeAttribute('aria-controls');
        trigger.removeAttribute('aria-expanded');
      });
      if (!document.querySelector('.lpl-careers__dialog[open]')) {
        document.body.classList.remove('lpl-careers-dialog-open');
      }
    }

    openFromTrigger(event, trigger) {
      if (event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.ctrlKey
        || event.shiftKey
        || event.altKey) return;

      const dialogId = trigger.getAttribute('aria-controls');
      const candidateDialog = dialogId ? document.getElementById(dialogId) : null;
      const dialog = candidateDialog && this.contains(candidateDialog) ? candidateDialog : null;
      if (!dialog || typeof dialog.showModal !== 'function') return;

      event.preventDefault();
      if (dialog.open) return;

      this.returnFocusTo = trigger;
      this.activeTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('lpl-careers-dialog-open');
      dialog.showModal();

      const closeButton = dialog.querySelector('[data-lpl-careers-close]');
      if (closeButton) requestAnimationFrame(() => closeButton.focus({ preventScroll: true }));
    }

    closeFromBackdrop(event, dialog) {
      if (event.target !== dialog) return;

      const bounds = dialog.getBoundingClientRect();
      const outside = event.clientX < bounds.left
        || event.clientX > bounds.right
        || event.clientY < bounds.top
        || event.clientY > bounds.bottom;

      if (outside) dialog.close();
    }

    trapFocus(event, dialog) {
      if (event.key !== 'Tab') return;

      const focusable = Array.from(dialog.querySelectorAll([
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(','))).filter((element) => element.getClientRects().length > 0);

      if (!focusable.length) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !dialog.contains(active))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    afterClose(dialog) {
      if (this.activeTrigger?.getAttribute('aria-controls') === dialog.id) {
        this.activeTrigger.setAttribute('aria-expanded', 'false');
      }
      if (!document.querySelector('.lpl-careers__dialog[open]')) {
        document.body.classList.remove('lpl-careers-dialog-open');
      }
      if (this.returnFocusTo?.isConnected) {
        this.returnFocusTo.focus({ preventScroll: true });
      }
      this.returnFocusTo = null;
      this.activeTrigger = null;
    }
  }

  customElements.define(elementName, LplCareers);
})();
